<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { fetchMarketplaceJson } from 'dashboard/helpers/marketplaceApi';
import { formatCurrency } from '../helpers/orderHelpers';

const route = useRoute();
const router = useRouter();

const isLoading = ref(false);
const loadError = ref('');
const products = ref([]);
const search = ref('');

const load = async () => {
  isLoading.value = true;
  loadError.value = '';
  try {
    const data = await fetchMarketplaceJson(
      route.params.accountId,
      'meta_ads/products'
    );
    products.value = data.products || [];
  } catch {
    loadError.value = 'Ürün verisi yüklenemedi.';
  } finally {
    isLoading.value = false;
  }
};

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return products.value;
  return products.value.filter(
    p => String(p.code).includes(q) || (p.title || '').toLowerCase().includes(q)
  );
});

const num = n => Number(n || 0).toLocaleString('tr-TR');

const openProduct = code =>
  router.push({ name: 'product_intel_detail', params: { code } });

onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header
      class="flex items-center justify-between gap-4 px-5 py-4 border-b sm:px-7 border-n-weak flex-shrink-0"
    >
      <div class="min-w-0">
        <h1 class="text-2xl font-bold text-n-slate-12">Ürün Zekâsı</h1>
        <p class="text-base text-n-slate-11">
          Reklam verdiğin ürünleri tek tek değerlendir — reklam, satış, stok ve
          kâr aynı yerde
        </p>
      </div>
      <button
        type="button"
        class="flex items-center gap-2 px-4 py-2.5 text-base font-semibold rounded-lg text-n-slate-12 outline outline-1 outline-n-weak hover:bg-n-alpha-1 flex-shrink-0"
        :disabled="isLoading"
        @click="load"
      >
        <span
          class="size-5 i-lucide-refresh-cw"
          :class="{ 'animate-spin': isLoading }"
        />
        Yenile
      </button>
    </header>

    <div class="flex-1 overflow-y-auto">
      <div class="px-5 py-6 mx-auto max-w-5xl sm:px-7">
        <input
          v-model="search"
          type="text"
          placeholder="Ürün kodu veya adı ara…"
          class="w-full px-4 py-3 mb-6 text-lg rounded-xl bg-n-solid-2 text-n-slate-12 outline outline-1 outline-n-weak"
        />

        <div
          v-if="isLoading"
          class="flex flex-col items-center justify-center gap-3 py-20 text-n-slate-11"
        >
          <Spinner />
          <p class="text-base">Ürünler yükleniyor…</p>
        </div>

        <div
          v-else-if="loadError"
          class="flex flex-col items-center gap-3 py-20 text-n-slate-11"
        >
          <span class="size-10 i-lucide-alert-triangle text-n-ruby-9" />
          <p class="text-base">{{ loadError }}</p>
        </div>

        <div v-else class="flex flex-col gap-3">
          <button
            v-for="p in filtered"
            :key="p.code"
            type="button"
            class="flex items-center gap-4 p-4 text-left rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak hover:bg-n-alpha-1"
            @click="openProduct(p.code)"
          >
            <img
              v-if="p.image"
              :src="p.image"
              :alt="p.title"
              class="flex-shrink-0 object-cover rounded-lg size-16 bg-n-solid-1"
            />
            <div
              v-else
              class="flex-shrink-0 flex items-center justify-center rounded-lg size-16 bg-n-solid-1"
            >
              <span class="size-7 i-lucide-shirt text-n-slate-9" />
            </div>

            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <span class="text-lg font-bold text-n-slate-12">{{
                  p.code
                }}</span>
                <span
                  v-if="p.messageFocused"
                  class="px-2 py-0.5 text-xs font-bold rounded-full bg-n-brand/15 text-n-brand"
                  title="Reklamların çoğu mesaj dönüşüm amaçlı"
                >
                  mesaj odaklı
                </span>
                <span
                  v-if="p.activeCampaignCount > 1"
                  class="px-2 py-0.5 text-xs font-bold rounded-full bg-n-amber-9/15 text-n-amber-11"
                  title="Aynı ürüne birden fazla aktif kampanya — yamyamlık riski"
                >
                  {{ p.activeCampaignCount }} aktif kampanya
                </span>
              </div>
              <p class="text-base truncate text-n-slate-11">{{ p.title }}</p>
            </div>

            <div class="flex-shrink-0 hidden gap-6 text-right sm:flex">
              <div>
                <p class="text-xs text-n-slate-11">Harcama</p>
                <p class="text-base font-bold text-n-slate-12">
                  {{ formatCurrency(p.totalSpend) }}
                </p>
              </div>
              <div>
                <p class="text-xs text-n-slate-11">Mesaj</p>
                <p class="text-base font-bold text-n-slate-12">
                  {{ num(p.messages) }}
                </p>
              </div>
              <div>
                <p class="text-xs text-n-slate-11">Satış</p>
                <p class="text-base font-bold text-n-slate-12">
                  {{ num(p.unitsSold) }}
                </p>
              </div>
              <div>
                <p class="text-xs text-n-slate-11">Stok</p>
                <p class="text-base font-bold text-n-slate-12">
                  {{ num(p.totalStock) }}
                </p>
              </div>
            </div>
            <span
              class="flex-shrink-0 size-5 i-lucide-chevron-right text-n-slate-10"
            />
          </button>

          <p
            v-if="!filtered.length"
            class="py-16 text-base text-center text-n-slate-11"
          >
            Eşleşen ürün bulunamadı.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
